import { getQueryParams } from '@/infrastructure/Utils'
import { language, isFlagActive } from '@/infrastructure/Configuration'
import { DateFactory, translate } from 'rhg-vue-common'

function _hasCancellationPolicy(booking, cancellationPolicyType) {
  return (
    booking.stay?.rate?.conditions?.cancellationPoliciesExtended?.types?.indexOf(
      cancellationPolicyType
    ) > -1
  )
}

function _dateFrom(dateString, pattern, format) {
  return DateFactory.formatDate(DateFactory.dateFromPattern(dateString, pattern), format)
}

const translateMealPlan = mealPlanCode => {
  const mealPlanType = {
    BB: translate('boBreakfastIncluded'),
    RO: translate('boRoomOnly'),
    HB: translate('chHalfBoardRate'),
    FB: translate('chFullBoard'),
    AI: translate('chAllInclusive'),
    HBL: translate('rdBreakfastAndLunchIncluded')
  }
  return mealPlanType[mealPlanCode]
}

export default {
  isAuthenticated: () => global.Cookie.read('auth') === 'true',
  bookingHeader: () => {
    const bookingHeader = global.appGlobalProps?.bookingHeaderInfo
    if (bookingHeader?.bookingId) {
      return bookingHeader
    }

    const { firstName, lastName, bookingId } = getQueryParams(window.location.search)
    if (bookingId) {
      return { firstName, lastName, bookingId }
    }

    return null
  },
  booking: state => state.bookingDetails,
  hotel: state => state.hotelDetails.hotel,
  bookingCheckinCheckoutInfo: state => state.bookingCheckinCheckoutInfo,
  dynamicLinkList: state => state.dynamicLinkList,
  reservationInfo: ({ bookingDetails, hotelDetails }) => {
    return {
      ...bookingDetails,
      bookingId: bookingDetails.bookingId,
      checkInDate: bookingDetails.stay.checkInDate,
      checkOutDate: bookingDetails.stay.checkOutDate,
      ableToModifyDates: bookingDetails.stay.ableToModifyDates,
      benefits: hotelDetails.benefits?.filter(b => b.showWhenIsActive && b.showWhenIsUpcoming),
      crowd: bookingDetails.stay.rate.crowd,
      pointsEarned: bookingDetails.priceSummary.totalPointsEarned,
      hotelInfo: {
        ...hotelDetails.hotel,
        coBrandImage: {
          OriginalLogoImg: hotelDetails.cobrandImage?.originalLogoImg
        }
      },
      bookingRestriction:
        isFlagActive('default.modify.prepaid') && bookingDetails?.bookingRestriction
    }
  },
  hotelLoading: state => state.hotelDetails.loading,
  bookingLoading: state => state.bookingDetails.loading,
  dynamicLinkLoading: state => state.dynamicLinkLoading,
  roomReservationIds: ({ bookingDetails }) =>
    bookingDetails.rooms
      .map(b => b.reservationId)
      .filter(id => !!id)
      .join(','),
  getRateDetails: ({ bookingDetails: { stay: { rate: { mealPlan = 'RO' } = {} } = {} } = {} }) => {
    return translateMealPlan(mealPlan)
  },
  getRoomMealPlans: ({ bookingDetails: { rooms = [] } = {} }) => {
    const roomMealPlans = {}
    rooms.forEach(room => {
      const { mealPlan = 'RO' } = room
      const translatedMealPlan = translateMealPlan(mealPlan)
      roomMealPlans[room.reservationId] = translatedMealPlan
    })
    return roomMealPlans
  },
  isFreeCancellationBefore: ({ bookingDetails }) => {
    return _hasCancellationPolicy(bookingDetails, 'FREE_CANCELLATION_BEFORE')
  },
  getFreeCancellationUpTo: ({ bookingDetails }) => {
    return language === 'tr'
      ? _dateFrom(
        bookingDetails.stay.rate.conditions.cancellationPoliciesExtended.freeCancellationUpTo,
        'YYYY-MM-DDThh:mm:ss',
        'MMMM DD, YYYY, HH:mm'
      )
      : _dateFrom(
        bookingDetails.stay.rate.conditions.cancellationPoliciesExtended.freeCancellationUpTo,
        'YYYY-MM-DDThh:mm:ss',
        'MMMM DD, YYYY, ha'
      )
  },
  subtotalPrice: ({
    bookingDetails: { priceSummary: { subtotal: { formattedAmount = null } = {} } = {} } = {}
  }) => {
    return formattedAmount
  },
  totalDiscountApplied: ({
    bookingDetails: { priceSummary: { totalDiscountApplied = null } = {} } = {}
  }) => {
    return totalDiscountApplied
  },
  subtotalDiscountApplied: ({
    bookingDetails: { priceSummary: { subtotalDiscountApplied = null } = {} } = {}
  }) => {
    return subtotalDiscountApplied
  },
  promotionCode: ({ bookingDetails: { promotionCode } = {} }) => {
    return promotionCode
  },
  subtractedEcertPrice: ({
    bookingDetails: { priceSummary: { subtractedEcertPrice = null } } = {}
  }) => {
    return subtractedEcertPrice
  },
  estimatedTaxesFormatted: ({
    bookingDetails: { priceSummary: { taxes: { formattedAmount = null } = {} } = {} } = {}
  }) => {
    return formattedAmount
  },
  additionalFeeFormatted: ({
    bookingDetails: { priceSummary: { additionalFees: { formattedAmount = null } = {} } = {} } = {}
  }) => {
    return formattedAmount
  },
  totalPoints: ({ bookingDetails }) => {
    return bookingDetails.priceSummary?.totalPoints
  },
  totalWithTaxFormatted: ({
    bookingDetails: { priceSummary: { totalWithTax: { formattedAmount = null } = {} } = {} } = {}
  }) => {
    return formattedAmount
  },
  totalWithTax: ({ bookingDetails: { priceSummary: { totalWithTax = {} } = {} } = {} }) => {
    return totalWithTax
  },
  isActiveBooking: ({ bookingDetails }) => {
    return bookingDetails.category === 'ACTIVE' && isFlagActive('booking.active.reservation')
  },
  allBookingCancelled: ({ bookingDetails }) => {
    return bookingDetails?.allCancelableBooking
  },
  showPaymentCompleted({ bookingDetails }) {
    return (
      !bookingDetails?.onlinePayments?.length &&
      !!bookingDetails?.onlinePaymentComplete?.length &&
      bookingDetails?.onlinePaymentComplete[0]?.complete
    )
  },
  hasPendingCharges({ bookingDetails }, getters) {
    const hasOnlinePayment = bookingDetails?.onlinePayments?.some(
      payment => !payment?.complete && Number(payment?.amount) > 0
    )

    const totalAmount = Number(bookingDetails?.priceSummary?.totalWithTax?.amount)

    return !getters.showPaymentCompleted && (!!hasOnlinePayment || totalAmount > 0)
  }
}
